/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {registerWithEmailAndPassword} from '../core/_requests'
import {Link, useNavigate} from 'react-router-dom'
import {signInWithPopup, updateProfile} from 'firebase/auth'
import {updateDoc} from '@firebase/firestore'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import {useAuth} from '../core/Auth'
import {AuthModel, UserModel} from '../core/_models'
import {v4 as uuid} from 'uuid'
import {useLocation} from 'react-router-dom'

import {
  addDoc,
  collection,
  doc,
  getDocs,
  getFirestore,
  setDoc,
  where,
  query,
} from '@firebase/firestore'
import db, {auth1, googleProvider} from '../../../../services/firebase'
import Input from 'app/components/Input'
import Button from 'app/components/Button'

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  changepassword: '',
  acceptTerms: false,
  activationCode: '',
  account_type: '',
}

const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    letters: /[a-zA-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    symbols: /[^a-zA-Z0-9]/.test(password),
  }

  const passedChecks = Object.values(checks).filter(Boolean).length

  let strength = 'weak'
  if (passedChecks === 4 && password.length >= 12) {
    strength = 'strong'
  } else if (passedChecks === 4) {
    strength = 'medium'
  }

  return {
    checks,
    strength,
    valid: Object.values(checks).every(Boolean),
  }
}

const InvitationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Last name is required'),
  password: Yup.string()
    .required('Password is required')
    .test(
      'password-strength',
      'Password must contain at least 8 characters with letters, numbers, and symbols',
      (value) => {
        if (!value) return false
        const result = checkPasswordStrength(value)
        return result.valid
      }
    ),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
  account_type: Yup.string(),
})

const generateUniqueTenantId = () => {
  return uuid()
}

interface ParseData {
  tenant_id?: string | null
  employee_id?: string | null
  fullName?: string
  email?: string
  account_type?: string
}

export function Invitation() {
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser, googleSignIn} = useAuth()
  const [parseData, setParseData] = useState<ParseData | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function decodeBase64ToJson(base64String) {
      const jsonString = atob(base64String)
      return JSON.parse(jsonString)
    }
    const searchParams = new URLSearchParams(location.search)
    const token = searchParams.get('token')
    if (token) {
      const parsedToken = decodeBase64ToJson(token)
      setParseData(parsedToken)
    }
  }, [location.search])

  useEffect(() => {
    if (parseData) {
      formik.setValues({
        firstname: parseData.fullName?.split(' ')[0] || '',
        lastname: parseData.fullName?.split(' ')[1] || '',
        email: parseData.email || '',
        password: '',
        changepassword: '',
        acceptTerms: false,
        activationCode: '',
        account_type: parseData.account_type || '',
      })
    }
  }, [parseData])

  const formik = useFormik({
    initialValues,
    validationSchema: InvitationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const db = getFirestore()
        const authCredential = await registerWithEmailAndPassword(
          values.email,
          values.password,
          values.firstname,
          values.lastname,
          values.changepassword
        )
        let tenantId =
          parseData?.tenant_id != null && parseData?.tenant_id !== undefined
            ? parseData?.tenant_id
            : generateUniqueTenantId()
        await updateProfile(authCredential.user, {
          displayName: `${values.firstname} ${values.lastname}`,
        })

        if (!authCredential.user || !authCredential.user.uid) {
          throw new Error('User is not authenticated')
        }

        const authModel: AuthModel = {
          api_token: await authCredential.user.getIdToken(),
          email: authCredential.user.email,
          uid: authCredential.user.uid,
          userName: `${values.firstname} ${values.lastname}`,
          password: values.password,
          first_name: values.firstname,
          last_name: values.lastname,
          tenantId: tenantId,
          activationCode: '',
        }
        const userModel: UserModel = {
          uid: authModel.uid || '',
          userName: authModel.userName || '',
          api_token: authModel.api_token || '',
          password: authModel.password || '',
          email: authModel.email || '',
          first_name: authModel.first_name || '',
          last_name: authModel.last_name || '',
          tenantId: tenantId || '',
          account_type: parseData?.account_type || '',
          termsAccepted: values.acceptTerms,
          partnerMSAAccepted: values.acceptTerms, 
          termsAcceptedAt: values.acceptTerms ? new Date() : undefined,
          partnerMSAAcceptedAt: values.acceptTerms ? new Date() : undefined,
        }

        if (parseData?.employee_id && parseData?.tenant_id) {
          const employeeDocRef = doc(
            db,
            'tenants',
            parseData.tenant_id,
            'employees',
            parseData.employee_id
          )
          await updateDoc(employeeDocRef, {
            uid:
              authCredential.user.uid !== '' ? authCredential.user.uid || '' : authModel.uid || '',
          })
        } else {
          console.warn(
            'Employee ID or Tenant ID not found in invitation data. Unable to update employee document.'
          )
        }

        await setDoc(doc(db, 'users', authCredential.user.uid), userModel)

        navigate('/login', {state: {accountCreated: true}})
      } catch (error) {
        console.error(error)
        let errorMessage = 'The Invitation details are incorrect'
        saveAuth(undefined)
        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleGoogleSignUp = async () => {
    // e.preventDefault();
    setLoading(false)

    try {
      const result = await signInWithPopup(auth1, googleProvider)
      // const result = await googleSignIn()
      const user = result.user
      // You can customize the data you want to store in Firestore
      const userData = {
        apiToken: user.getIdToken(),
        uid: user.uid,
        email: user.email,
        userName: user.displayName,
        tenantId: user.tenantId,
        // activationCode:user.activationCode
      }

      const usersCollection = collection(db, 'users') // 'users' is the Firestore collection name
      const emailQuery = query(usersCollection, where('email', '==', user.email))
      const emailQuerySnapshot = await getDocs(emailQuery)
      if (!emailQuerySnapshot.empty) {
        return
      }
      await addDoc(usersCollection, userData)
      setCurrentUser(userData)
    } catch (error) {
      console.error('Google sign-up error:', error)
    }
  }

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  useEffect(() => {
    if (formik.status) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
  }, [formik.status])

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_signup_form'
        onSubmit={formik.handleSubmit}
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          {/* begin::Title */}
          <h1 className='text-dark fw-bolder mb-5 mt-2'>Invitation</h1>
          {/* end::Title */}

          <div className='text-gray-500 fw-semibold fs-6'>
            {' '}
            Safeguarding Your Data with Robust Security Solutions.
          </div>
        </div>
        {/* end::Heading */}

        {/* begin::Login options */}
        {/* <div className='justify-content-center  row g-3 mb-9'> */}

        {/* <div className='col-md-9'>
    
          <button
            onClick={handleGoogleSignUp}
            type='button'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
              className='h-15px me-3'
            />
            Sign in with Google
          </button>
          {errorMessage && (
            <div className='error-message mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{errorMessage}</div>
            </div>
          )}

        </div> */}
        {/* end::Col */}

        {/* begin::Col */}
        {/* <div className='col-md-6'>
          <a
            href='#'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')}
              className='theme-light-show h-15px me-3'
            />
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black-dark.svg')}
              className='theme-dark-show h-15px me-3'
            />
            Sign in with Apple
          </a>
        </div> */}
        {/* </div> */}
        {/* end::Login options */}

        {/* <div className='separator separator-content my-14'>
        <span className='w-125px text-gray-500 fw-semibold fs-7'>Or with email</span>
      </div> */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group Firstname */}
        <div className='fv-row mb-8'>
          <Input
            type='text'
            label='First Name'
            placeholder='First Name'
            className={`bg-transparent ${
              formik.touched.firstname && !formik.errors.firstname && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            {...formik.getFieldProps('firstname')}
            error={
              formik.touched.firstname && formik.errors.firstname ? formik.errors.firstname : ''
            }
          />
        </div>
        {/* end::Form group */}
        <div className='fv-row mb-8'>
          {/* begin::Form group Lastname */}
          <Input
            type='text'
            label='Last Name'
            placeholder='Last Name'
            className={`bg-transparent ${
              formik.touched.lastname && !formik.errors.lastname && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            {...formik.getFieldProps('lastname')}
            error={formik.touched.lastname && formik.errors.lastname ? formik.errors.lastname : ''}
          />
          {/* end::Form group */}
        </div>

        {/* begin::Form group Email */}
        <div className='fv-row mb-8'>
          <Input
            type='email'
            label='Email'
            placeholder='Email'
            className={`bg-transparent ${
              formik.touched.email && !formik.errors.email && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            {...formik.getFieldProps('email')}
            error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
          />
        </div>
        {/* end::Form group */}

        {/* begin::Form group Password */}
        <div className='fv-row mb-8' data-kt-password-meter='true'>
          <div className='mb-1'>
            <div className='mb-3'>
              <Input
                type='password'
                label='Password'
                placeholder='Password'
                className={`bg-transparent ${
                  formik.touched.password && !formik.errors.password && 'is-valid'
                }`}
                labelClassName='fw-bolder'
                {...formik.getFieldProps('password')}
                error={
                  formik.touched.password && formik.errors.password ? formik.errors.password : ''
                }
              />
            </div>
            {/* begin::Meter */}
            <div
              className='d-flex align-items-center mb-3'
              data-kt-password-meter-control='highlight'
            >
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
            </div>
            {/* end::Meter */}
          </div>
          <div className='text-muted'>
            Use 8 or more characters with a mix of letters, numbers & symbols.
          </div>
        </div>
        {/* end::Form group */}

        {/* begin::Form group Confirm password */}
        <div className='fv-row mb-5'>
          <Input
            type='password'
            label='Confirm Password'
            placeholder='Password Confirmation'
            className={`bg-transparent ${
              formik.touched.changepassword && !formik.errors.changepassword && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            {...formik.getFieldProps('changepassword')}
            error={
              formik.touched.changepassword && formik.errors.changepassword
                ? formik.errors.changepassword
                : ''
            }
          />
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'>
            <input
              className='form-check-input'
              type='checkbox'
              id='kt_login_toc_agree'
              {...formik.getFieldProps('acceptTerms')}
            />
            <span>
              I accept the&nbsp;
              <a
                href='https://getsecureslate.com/policy#terms'
                target='_blank'
                className='link-primary fw-semibold'
              >
                Terms of Service
              </a>
              &nbsp;and&nbsp;
              <a
                href='https://getsecureslate.com/policy#partner-msa'
                target='_blank'
                className='link-primary fw-semibold'
              >
                Partner MSA
              </a>
            </span>
          </label>
          {formik.touched.acceptTerms && formik.errors.acceptTerms && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.acceptTerms}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='text-center'>
          <Button
            type='submit'
            btnType='primary'
            id='kt_sign_up_submit'
            className='w-100 mb-4'
            style={{height: '43.2px'}}
            disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
          >
            {!loading && <span className='indicator-label'>Submit</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
          <span className='text-gray-500 text-center fw-semibold fs-6'>
            Already have an account?{' '}
            <Link to='/auth/login' className='link-primary mx-2'>
              {/* <button
            type='button'
            id='kt_login_signup_form_cancel_button'
            className='btn btn-lg btn-light-primary w-100 mb-2'
          >
            Cancel
          </button> */}
              Login
            </Link>
          </span>
        </div>
        {/* end::Form group */}
      </form>
    </>
  )
}
